import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Search, Tag } from "@carbon/react";
import { CategoryType, CategoryFilterResponse } from "../../types";
import { useOpportunityFilters } from "../../hooks/useOpportunityDiscovery";
import { Loader, NetworkError } from "..";
import "./opportunityFilters.scss";

interface OpportunityFiltersProps {
  type: CategoryType;
  selectedFilters: string[];
  onFiltersChange: (filters: string[]) => void;
}

/**
 * Rebuilt on Carbon (Task 5, Opportunity Discovery + AI Profiling flow). The pre-Carbon panel was
 * an always-visible, scrollable checkbox list (not a closed multi-select dropdown), so it maps to
 * Carbon `Checkbox` + `Search`, not `FilterableMultiSelect`. The `categoryFilters` contract this
 * emits to `OpportunityDiscovery` (`containers/OpportunityDiscovery/OpportunityDiscovery.tsx`,
 * which drives `useOpportunities`) is UNCHANGED: `onFiltersChange` still fires with the full next
 * array of selected `categoryId`s — toggle adds/removes one id, "Select All" sends every id from
 * the current (unfiltered-by-search) `filters` response, "Clear All" sends `[]`. Nothing about
 * shape or timing changed, only the markup.
 */
const OpportunityFilters = ({
  type,
  selectedFilters,
  onFiltersChange,
}: OpportunityFiltersProps) => {
  const { t } = useTranslation();
  const { data: filters, isLoading, error } = useOpportunityFilters(type);
  const [searchTerm, setSearchTerm] = useState("");

  const handleFilterToggle = (categoryId: string) => {
    const newFilters = selectedFilters.includes(categoryId)
      ? selectedFilters.filter((id) => id !== categoryId)
      : [...selectedFilters, categoryId];
    onFiltersChange(newFilters);
  };

  const handleSelectAll = () => {
    if (!filters) return;
    onFiltersChange(filters.map((filter) => filter.categoryId));
  };

  const handleClearAll = () => {
    onFiltersChange([]);
  };

  const filteredFilters =
    filters?.filter(
      (filter) =>
        filter.categoryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        filter.categoryNameEn?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  if (isLoading) {
    return (
      <div className="opportunity-filters opportunity-filters--loading">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="opportunity-filters">
        <NetworkError
          title={t("opportunities.filters.error")}
          message={t("opportunities.filters.errorDescription")}
        />
      </div>
    );
  }

  return (
    <div className="opportunity-filters">
      <div className="opportunity-filters__header">
        <h3 className="opportunity-filters__title">
          {t("opportunities.filters.title")}
        </h3>
        <div className="opportunity-filters__header-actions">
          <Button kind="ghost" size="sm" onClick={handleSelectAll}>
            {t("opportunities.filters.selectAll")}
          </Button>
          <Button kind="ghost" size="sm" onClick={handleClearAll}>
            {t("opportunities.filters.clearAll")}
          </Button>
        </div>
      </div>

      <Search
        id="opportunity-filters-search"
        size="md"
        labelText={t("opportunities.filters.searchPlaceholder")}
        placeholder={t("opportunities.filters.searchPlaceholder")}
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        onClear={() => setSearchTerm("")}
      />

      <fieldset className="opportunity-filters__list">
        {filteredFilters.length === 0 ? (
          <p className="opportunity-filters__no-results">
            {t("opportunities.filters.noResults")}
          </p>
        ) : (
          filteredFilters.map((filter) => (
            <OpportunityFilterItem
              key={filter.categoryId}
              filter={filter}
              isSelected={selectedFilters.includes(filter.categoryId)}
              onToggle={() => handleFilterToggle(filter.categoryId)}
            />
          ))
        )}
      </fieldset>

      {selectedFilters.length > 0 && (
        <p className="opportunity-filters__selected-count">
          {t("opportunities.filters.selectedCount", {
            count: selectedFilters.length,
          })}
        </p>
      )}
    </div>
  );
};

interface OpportunityFilterItemProps {
  filter: CategoryFilterResponse;
  isSelected: boolean;
  onToggle: () => void;
}

const OpportunityFilterItem = ({
  filter,
  isSelected,
  onToggle,
}: OpportunityFilterItemProps) => {
  const { t } = useTranslation();

  return (
    <div className="opportunity-filters__item">
      <Checkbox
        id={`opportunity-filter-${filter.categoryId}`}
        checked={isSelected}
        onChange={onToggle}
        labelText={
          <span className="opportunity-filters__item-label">
            <span className="opportunity-filters__item-name">
              {filter.categoryName}
            </span>
            {filter.categoryNameEn !== filter.categoryName && (
              <span className="opportunity-filters__item-name-en">
                {filter.categoryNameEn}
              </span>
            )}
          </span>
        }
      />
      <div className="opportunity-filters__item-meta">
        {filter.isAssignedToSupplier && (
          <Tag type="green" size="sm">
            {t("opportunities.filters.assigned")}
          </Tag>
        )}
        <span className="opportunity-filters__item-count">
          {filter.materialCount}
        </span>
      </div>
    </div>
  );
};

export default OpportunityFilters;
